import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import moment from "moment";

export function JobCard({ job, dark }) {
  const salaryText = () => {
    if (job.salary_type === "R" && job.minsalary)
      return `${job.jobcur} ${job.minsalary}–${job.maxsalary} / ${job.salary_unit}`;
    if (job.salary_type === "F" && job.minsalary)
      return `${job.jobcur} ${job.minsalary} / ${job.salary_unit}`;
    return "Negotiable";
  };

  const cardClass = "sf-job-card" + (dark ? " sf-job-card-dark" : "");

  return (
    <div className={cardClass}>
      <div className="sf-job-card-top">
        <Link to={"/company/" + job.clientslug} className="sf-job-logo">
          <img
            src={job.photo ? "/uploads/emp_img/" + job.photo : "/default/default_company_logo.jpg"}
            alt={job.comp_name}
          />
        </Link>
        <div style={{ minWidth: 0 }}>
          <Link to={"/company/" + job.clientslug} className="sf-job-company">
            {job.comp_name}
          </Link>
          <div className="sf-job-posted">{moment(job.created_at).fromNow()}</div>
        </div>
      </div>

      <Link to={"/jobs/" + job.slug} className="sf-job-title">
        {job.job_title}
      </Link>

      <div className="sf-job-meta">
        {job.job_location && (
          <span className="sf-job-meta-item">
            <i className="fa fa-map-marker" /> {job.job_location}
          </span>
        )}
        {job.job_type && (
          <span className="sf-job-meta-item">
            <i className="fa fa-building-o" /> {job.job_type}
          </span>
        )}
      </div>

      {job.work_type && (
        <div className="sf-job-badges">
          <span className={"sf-badge " + (dark ? "sf-badge-white" : "sf-badge-blue")}>
            {job.work_type}
          </span>
        </div>
      )}

      <div className="sf-job-footer">
        <span className="sf-job-salary">{salaryText()}</span>
        <Link to={"/jobs/" + job.slug} className="sf-job-browse">
          Browse Job
        </Link>
      </div>
    </div>
  );
}

export default function JobsList({ title, size, extraParams }) {
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEmployer, setExpandedEmployer] = useState(null);

  const perPage = size || 20;
  const extra = extraParams || {};

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    const params = new URLSearchParams({ per_page: perPage, ...extra });
    axios
      .get(constvar.api_url + "jobs/grouped?" + params.toString(), {
        signal: controller.signal,
      })
      .then((res) => {
        if (!isMounted) return;
        if (res.data.status === "success") setEmployers(res.data.data.data || []);
      })
      .catch((error) => {
        if (!isMounted) return;
        if (error.name !== "AbortError" && error.name !== "CanceledError") {
          console.error("Failed to load jobs:", error);
        }
      })
      .finally(() => setLoading(false));

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [perPage, JSON.stringify(extra)]);

  const handleEmployerHover = (employerId) => {
    setExpandedEmployer(employerId);
  };

  const handleEmployerLeave = () => {
    setExpandedEmployer(null);
  };

  return (
    <section className="sf-section">
      <div className="sf-container">
        <div className="sf-section-header">
          <h2 className="sf-section-title">{title}</h2>
          <Link to="/job_search" className="sf-section-link">
            View All
          </Link>
        </div>

        {loading ? (
          <div className="sf-loading">
            <i className="fa fa-spinner fa-spin" /> Loading…
          </div>
        ) : employers.length > 0 ? (
          <div className="sf-groupby-employers-grid">
            {employers.map((employer) => {
              const isExpanded = expandedEmployer === employer.employer_id;
              const visibleJobs = isExpanded ? employer.jobs.slice(0, 7) : employer.jobs.slice(0, 2);
              const hasMoreJobs = employer.jobs.length > 2;
              const remainingCount = employer.jobs.length - 2;

              return (
                <div className="jobs_card">
                  <div
                    key={employer.employer_id}
                    className={`sf-groupby-employer-card ${isExpanded ? "sf-groupby-employer-card-expanded" : ""}`}
                    onMouseEnter={() => handleEmployerHover(employer.employer_id)}
                    onMouseLeave={handleEmployerLeave}
                  >
                    <div className="flex justify-between">
                      {/* Employer Header */}
                      <div className="sf-groupby-employer-header">
                        <div className="sf-groupby-employer-logo-wrapper">
                          <img
                            src={
                              employer.photo ||
                              "/default/default_company_logo.jpg"
                            }
                            alt={employer.comp_name}
                            className="sf-groupby-employer-logo"
                          />
                        </div>
                        <div className="sf-groupby-employer-name">
                          <h5><Link key={employer.employer_id} to={`/company/${employer.clientslug}`} className="sf-groupby-employer-name-link">{employer.comp_name || "N/A"}</Link></h5>
                          {visibleJobs.map((job) => (
                            <Link
                              key={job.id}
                              to={"/jobs/" + job.slug}
                              className="sf-groupby-employer-job-link"
                            >
                                {job.job_title.length > 25 
                                  ? `${job.job_title.substring(0, 25)}...` 
                                  : job.job_title
                                }
                            </Link>
                          ))}
                        </div>
                      </div>
                      {/* Expand Button - Show only if more than 2 jobs */}
                      {hasMoreJobs && (
                        <div className="sf-groupby-employer-expand-button">
                          <Link key={employer.employer_id} to={`/company/${employer.clientslug}`}>
                          <span className="sf-groupby-expand-text">
                          +{remainingCount} more
                          </span>
                          <i className="fa fa-chevron-down" />
                          </Link>
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="sf-empty">No jobs available at the moment.</div>
        )}
      </div>
    </section>
  );
}
